import { HTTP_STATUS_TOO_MANY_REQUESTS } from "@frontend-shared/constants";
import type { IServerMessageTranslator } from "@frontend-shared/utils";
import {
  describeRequestErrorDetails,
  getUploadFailureDetails,
  isTokenBudgetExceededError,
  translateServerMessage,
} from "@frontend-shared/utils";
import type { IProbeUploadFailure } from "@frontend-shared/utils/probes";
import { isAxiosError } from "axios";
import { toast } from "sonner";

import i18n from "@/i18n";

import type { IHandleErrorToastProps } from "./types";

// NOTE: NSAI-6252 — the key-to-translation step lives in frontend-shared so
// mobile resolves backend error keys the same way; this wrapper binds it to the
// web app's own i18n instance.
const serverMessageTranslator: IServerMessageTranslator = {
  exists: (key) => i18n.exists(key),
  // NOTE: `as never` matches the repo idiom for runtime-dynamic i18n keys that
  // can't be narrowed against i18next's strict literal-key types.
  t: (key) => String(i18n.t(key as never)),
};

const translateMessage = (message: string): string =>
  translateServerMessage({ message, translator: serverMessageTranslator });

// NOTE: Pulls the localized message out of a NestJS-shaped axios error
// (`{statusCode, message, error}`) without showing a toast. Used by callers
// that need to display the error inline (e.g. next to a failed upload row)
// instead of in a transient toast. Returns null when the error is not an
// axios error or has no extractable message.
//
// Order matches the precedence handleErrorToast historically used:
//   1. string `message` field — the typical NestJS shape, always wins
//   2. 429 (rate-limited) without a string body — fall back to the
//      generic "too_many_requests" hint so the user sees something useful
//   3. array `message` field — class-validator returns an array of
//      validation errors; surface the first
//   4. otherwise null — caller decides on a fallback
export const extractServerErrorMessage = (error: unknown): string | null => {
  if (!isAxiosError(error)) return null;

  const serverMessage = error.response?.data?.message;
  if (typeof serverMessage === "string") {
    return translateMessage(serverMessage);
  }

  if (error.response?.status === HTTP_STATUS_TOO_MANY_REQUESTS) {
    return i18n.t("common:too_many_requests");
  }

  if (
    Array.isArray(serverMessage) &&
    serverMessage.length > 0 &&
    typeof serverMessage[0] === "string"
  ) {
    return translateMessage(serverMessage[0]);
  }

  return null;
};

export const handleErrorToast = ({
  error,
  fallbackMessage,
}: IHandleErrorToastProps): void => {
  const message =
    extractServerErrorMessage(error) ??
    (typeof fallbackMessage === "string" ? fallbackMessage : null);

  if (message) {
    toast.error(message);
  }
};

// NOTE: NSAI-4876 — the shared uploader tags a rejection as REJECTED / NETWORK /
// STALLED with i18n params already filled in. This turns that tag into a
// sentence, falling back to whatever the server said. Returns null when the
// error explains nothing, which is the caller's cue to supply its own wording
// rather than render an empty line. Mirrors `resolveUploadFailureReason` in the
// mobile app — worth converging into `frontend-shared` once the shared package
// has a test runner (NSAI-4197 sibling).
export const resolveUploadFailureReason = (error: unknown): string | null => {
  const uploadFailure = getUploadFailureDetails(error);

  if (uploadFailure) {
    return i18n.t(uploadFailure.i18nKey, uploadFailure.i18nParams);
  }

  const serverMessage = extractServerErrorMessage(error);

  return serverMessage?.trim() ? serverMessage : null;
};

// NOTE: NSAI-5732 — the probe upload card reads a failure in three parts, the
// same way the mobile card has since NSAI-4880: the sentence the reader gets,
// the status line behind "View details", and whether the refusal was a spent
// usage limit. Web used to show a fixed "try a different file" line and hide
// the server's explanation inside the disclosure, so a budget refusal read as
// a broken document — with both remedies it offered impossible.
//
// The presign and the generate call answer as axios errors, but the S3 PUT
// between them throws NSAI-4883's tagged error, which only
// `resolveUploadFailureReason` understands — so reading the reason through it
// is what keeps this card honest about every leg of the upload.
export const describeProbeUploadFailure = (
  error: unknown,
): IProbeUploadFailure => ({
  reason: resolveUploadFailureReason(error),
  technicalDetails: describeRequestErrorDetails({
    error,
    noResponseText: i18n.t("common:no_server_response"),
  }),
  isUsageLimit: isTokenBudgetExceededError(error),
});
